import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileCheck2, Plus } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, ActionButton, useLoad, fmt } from "@/components/control/kit";

export const contentFields = (c = {}) => [
  { name: "title", label: "Titel", required: true, default: c.title },
  { name: "summary", label: "Kurztext (Vorschau in der App)", type: "textarea", rows: 2, required: true, default: c.summary },
  { name: "body", label: "Text", type: "textarea", rows: 8, required: true, default: c.body },
  { name: "audience", label: "Zielgruppe", type: "select", required: true, default: c.audience || "mitglieder", placeholder: false,
    options: [{ value: "mitglieder", label: "Mitglieder-App" }, { value: "öffentlich", label: "Öffentlich" }] },
];

const TABS = [["all", "Alle"], ["entwurf", "Entwurf"], ["in_pruefung", "In Prüfung"], ["rückfrage", "Rückfrage"],
  ["freigegeben", "Freigegeben"], ["geplant", "Geplant"], ["veröffentlicht", "Veröffentlicht"]];

export default function Content() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const tabs = useTabs(TABS.map(([value, label]) => ({ value, label })), status);
  const st = useLoad(`/content?status=${encodeURIComponent(status)}`);
  return (
    <div>
      <PageHeader title="Inhalte & Freigaben" subtitle="Beiträge verfassen, redaktionell prüfen und die Veröffentlichung planen." icon={FileCheck2}
        actions={<>
          <Link to="/inhalte-freigaben/textpruefung" data-testid="text-reviews-link" className="rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Verständlichkeit prüfen</Link>
          {st.data?.can.manage && (
          <ActionButton label="Inhalt erstellen" icon={Plus} variant="default" testId="create-content" fields={contentFields()} submitLabel="Entwurf speichern"
            page="neu" pageSubtitle="Erstelle einen verständlichen Artikel als Entwurf für die gewählte Zielgruppe."
            onSubmit={async (v) => { const { data } = await api.post("/content", v); navigate(`/inhalte-freigaben/${data.item.id}`, { replace: true }); return "Entwurf gespeichert."; }} />
          )}
        </>} />
      <Tabbar tabs={tabs} onChange={setStatus} testId="content-status-tabs" />
      <TabPanel tabs={tabs} testId="content-status-tabs-panel">
      <Section title="Inhalte" testId="content-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="content-table">
                <TableHeader><TableRow><TableHead>Titel</TableHead><TableHead>Zielgruppe</TableHead><TableHead>Autor:in</TableHead>
                  <TableHead>Geändert</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.items.length === 0 && <TableRow><TableCell colSpan={5}><Empty /></TableCell></TableRow>}
                  {d.items.map((c) => (
                    <TableRow key={c.id} data-testid={`content-row-${c.id}`}>
                      <TableCell><RowLink to={`/inhalte-freigaben/${c.id}`} testId={`content-link-${c.id}`}>{c.title}</RowLink></TableCell>
                      <TableCell className="text-sm">{c.audience === "öffentlich" ? "Öffentlich" : "Mitglieder-App"}</TableCell>
                      <TableCell className="text-sm">{c.author?.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmt(c.updated_at)}</TableCell>
                      <TableCell><Status value={c.status} />{c.publish_at && c.status === "geplant" && <p className="mt-1 text-xs text-muted-foreground">{fmt(c.publish_at)}</p>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
